import { useCallback, useEffect, useRef, useState } from "react";
import { Button, VolumeUpSVG } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import AILoadingSpinner from "@/components/AIComponents/AILoadingSpinner";
import { useGetTranslatedAudioFile } from "@/view/TiptapArticleBody/components/TranslateDrawer/utils";
import {
  iconCss,
  loadingSpinnerCss,
  textCss,
} from "./ListenToTranslationButton.styles";
import ListenToTranslationError from "./ListenToTranslationError";
import StopIcon from "./StopIcon.svg";

const ButtonWithTrackEvent = withTrackEvent(Button);

export interface ListenToTranslationButtonProps {
  text: string;
  languageCode: string;
}

const ListenToTranslationButton = ({
  text,
  languageCode,
}: ListenToTranslationButtonProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFirstAttempt, setIsFirstAttempt] = useState(true);

  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { currentAudioData, error, isLoading, fetchTranslatedAudioFile } =
    useGetTranslatedAudioFile();

  const handleClick = useCallback(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, [isPlaying]);

  useEffect(() => {
    fetchTranslatedAudioFile({
      text,
      languageCode,
    });
  }, [fetchTranslatedAudioFile, text, languageCode]);

  useEffect(() => {
    if (!currentAudioData || !audioRef.current) return;

    audioRef.current.src = currentAudioData.download_url;
    audioRef.current.onended = () => {
      setIsPlaying(false);
    };
  }, [currentAudioData]);

  if (isLoading) {
    return <AILoadingSpinner __cssFor={{ root: loadingSpinnerCss }} />;
  }

  if (error) {
    return (
      <ListenToTranslationError
        allowTryAgain={isFirstAttempt}
        languageCode={languageCode}
        onTryAgain={() => {
          fetchTranslatedAudioFile({
            text,
            languageCode,
          });
          setIsFirstAttempt(false);
        }}
      />
    );
  }

  return (
    <>
      <ButtonWithTrackEvent
        __cssFor={{
          root: textCss,
          Icon: { root: iconCss },
        }}
        icon={{
          SvgComponent: isPlaying ? StopIcon : VolumeUpSVG,
          alignment: Button.iconAlignments.left,
        }}
        onClick={handleClick}
        flavor={Button.flavors.tertiary}
        actionName={actionNames.interact}
        objectType={objectTypes.translation}
        streamProperties={{
          description: `Listen to translated text in language ${languageCode}`,
        }}
        dataProps={{ "data-qa-selector": `listen-to-translation` }}
      >
        {isPlaying ? "Stop" : "Listen"}
      </ButtonWithTrackEvent>
      <div style={{ display: "none" }}>
        <audio controls ref={audioRef}>
          Your browser does not support the <code>audio</code> element for read
          aloud mode.
        </audio>
      </div>
    </>
  );
};

export default ListenToTranslationButton;
